import type { ReactElement } from 'react';

import { Badge } from '@autop/ui/src/badge';

import { AssetDownloadButton } from '@/features/download-asset';
import { assetMetaOf, type LibraryAsset } from '@/entities/asset';
import {
  chapterScreenshotUrl,
  formatTimeSec,
  type VideoChapter,
  youtubeWatchUrl,
} from '@/entities/video';

/** 그 구간에서 쓰는 자료를 **그 자리에서** 준다. 맨 아래 몰아 두면 어느 단계 것인지 다시 짚어야 한다. */
function ChapterAsset({
  asset,
  resumePath,
}: {
  asset: LibraryAsset;
  resumePath: string;
}): ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-secondary/40 p-4">
      <Badge variant="mint">무료</Badge>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[14px] font-medium">{asset.title}</span>
        <span className="text-[12.5px] text-muted-foreground">{assetMetaOf(asset)}</span>
      </div>
      <AssetDownloadButton
        assetId={asset.id}
        resumePath={resumePath}
        isLink={asset.format === 'LINK'}
      />
    </div>
  );
}

export function ChapterSection({
  index,
  chapter,
  youtubeId,
  resumePath,
}: {
  index: number;
  chapter: VideoChapter;
  youtubeId: string;
  resumePath: string;
}): ReactElement {
  return (
    <section id={chapter.id} aria-labelledby={`${chapter.id}-heading`} className="scroll-mt-24">
      <div className="flex gap-4">
        <span
          aria-hidden="true"
          className="grid size-7 shrink-0 place-items-center rounded-full bg-primary font-mono text-[12px] font-semibold text-primary-foreground"
        >
          {index}
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2
              id={`${chapter.id}-heading`}
              className="text-[1.0625rem] font-semibold tracking-[-0.03em]"
            >
              {chapter.title}
            </h2>
            {/* timeSec 이 t= 딥링크의 근거다 (LIB-U-05 AC-2) — 시각을 누르면 그 구간부터 열린다 */}
            <a
              data-numeric=""
              href={youtubeWatchUrl(youtubeId, chapter.timeSec)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[12px] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {formatTimeSec(chapter.timeSec)} ↗
            </a>
          </div>

          <p className="text-[14px] leading-[1.9] text-muted-foreground">{chapter.body}</p>

          {chapter.hasScreenshot ? (
            <img
              src={chapterScreenshotUrl(chapter.id)}
              alt={`${chapter.title} 화면 캡처`}
              loading="lazy"
              className="w-full rounded-lg border border-border"
            />
          ) : null}

          {chapter.assets.map((asset) => (
            <ChapterAsset key={asset.id} asset={asset} resumePath={resumePath} />
          ))}
        </div>
      </div>
    </section>
  );
}
